import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { 
  Plus, 
  Search, 
  Package, 
  Image as ImageIcon,
  Sparkles,
  Eye,
  CheckCircle2,
  XCircle,
  Loader2,
  Filter
} from "lucide-react";

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/skus`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, { withCredentials: true });
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to load categories");
    }
  };

  const getImageStatusBadge = (imageStatus) => {
    if (!imageStatus) return null;
    const hasImages = Object.values(imageStatus).some(v => v);
    const hasFront = imageStatus.front;
    
    if (!hasImages) {
      return <Badge variant="outline" className="text-gray-500">No Images</Badge>;
    }
    if (!hasFront) {
      return <Badge variant="outline" className="text-yellow-600 border-yellow-300">Front Required</Badge>;
    }
    
    const count = Object.values(imageStatus).filter(v => v).length;
    return <Badge className="bg-green-100 text-green-700">{count}/4 Images</Badge>;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: "bg-gray-100 text-gray-700", label: "Draft" },
      ready: { color: "bg-blue-100 text-blue-700", label: "Ready" },
      processing: { color: "bg-yellow-100 text-yellow-700", label: "Processing" },
      completed: { color: "bg-green-100 text-green-700", label: "Completed" },
      failed: { color: "bg-red-100 text-red-700", label: "Failed" }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.sku_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.sku_code?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Products / SKUs</h1>
            <p className="text-gray-600">Manage your product catalog and images</p>
          </div>
          <Button 
            onClick={() => navigate("/products/new")}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Product
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by name or SKU code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-products"
                />
              </div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
                data-testid="category-filter"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No products yet</h3>
              <p className="text-gray-500 mb-4">Add your first product to start creating AI photoshoots</p>
              <Button onClick={() => navigate("/products/new")} data-testid="empty-add-product">
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProducts.map(product => (
              <Card 
                key={product.sku_id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/products/${product.sku_id}`)}
                data-testid={`product-card-${product.sku_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Product Thumbnail */}
                    <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {product.image_status?.front ? (
                        <img 
                          src={product.front_image_url || "https://placehold.co/80x80?text=Product"} 
                          alt={product.sku_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-gray-400" />
                      )}
                    </div>
                    
                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{product.sku_name}</h3>
                      <p className="text-sm text-gray-500">SKU: {product.sku_code || product.sku_id.slice(-8)}</p>
                      <p className="text-sm text-gray-500 capitalize">{product.category}</p>
                      
                      <div className="flex flex-wrap gap-2 mt-2">
                        {getStatusBadge(product.status)}
                        {getImageStatusBadge(product.image_status)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 mt-4 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/products/${product.sku_id}`);
                      }}
                      data-testid={`view-product-${product.sku_id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button 
                      size="sm"
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                      disabled={!product.image_status?.front}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/products/${product.sku_id}/shoot`);
                      }}
                      data-testid={`create-shoot-${product.sku_id}`}
                    >
                      <Sparkles className="w-4 h-4 mr-1" />
                      AI Shoot
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Products;
